import React, { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import { 
  differenceInDays, 
  parseISO, 
  startOfMonth, 
  endOfMonth,
  subMonths,
  addMonths,
  format,
  isWithinInterval,
  isFuture,
  isToday,
  addDays,
  isBefore,
  isAfter,
  startOfDay,
  endOfDay
} from 'date-fns';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Button } from '@/components/ui/button';
import { Download, Calendar as CalendarIcon } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Booking } from './BookingCalendar';
import { cn } from '@/lib/utils';
import { MonthlyRevenue } from './MonthlyRevenue';

type RevenueDashboardProps = {
  bookings: Booking[];
};

type TimeframeOption = '3m' | '6m' | '1y' | 'custom';

type AgeingBucket = {
  range: string;
  amount: number;
  count: number;
};

const timeframeOptions = [
  { value: '3m', label: '3 Months' },
  { value: '6m', label: '6 Months' },
  { value: '1y', label: '1 Year' },
  { value: 'custom', label: 'Custom Range' }
] as const;

export function RevenueDashboard({ bookings }: RevenueDashboardProps) {
  const [timeframe, setTimeframe] = React.useState<TimeframeOption>('1y');
  const [customDateRange, setCustomDateRange] = React.useState<{
    from: Date;
    to: Date;
  }>({
    from: subMonths(new Date(), 1),
    to: addMonths(new Date(), 11)
  });

  // Calculate date range based on selected timeframe
  const dateRange = useMemo(() => {
    const today = new Date();
    
    switch (timeframe) {
      case '3m':
        return {
          from: startOfDay(subMonths(today, 1)),
          to: endOfDay(addMonths(today, 2))
        };
      case '6m':
        return {
          from: startOfDay(subMonths(today, 2)),
          to: endOfDay(addMonths(today, 4))
        };
      case '1y':
        return {
          from: startOfDay(subMonths(today, 3)),
          to: endOfDay(addMonths(today, 9))
        };
      case 'custom':
        return {
          from: startOfDay(customDateRange.from),
          to: endOfDay(customDateRange.to)
        };
    }
  }, [timeframe, customDateRange]);

  // Calculate revenue metrics
  const {
    totalPaidRevenue,
    totalUpcomingRevenue,
    ageingBreakdown
  } = useMemo(() => {
    const today = new Date();
    const sevenDaysFromNow = addDays(today, 7);
    const thirtyDaysFromNow = addDays(today, 30);

    const filteredBookings = bookings.filter(booking => {
      const bookingDate = parseISO(booking.date);
      return isWithinInterval(bookingDate, {
        start: startOfMonth(dateRange.from),
        end: endOfMonth(dateRange.to)
      });
    });

    // Calculate total paid revenue
    const paidRevenue = filteredBookings
      .filter(b => b.status === 'paid' && b.revenue)
      .reduce((sum, b) => sum + (b.revenue?.amount || 0), 0);

    // Calculate total upcoming revenue (only confirmed bookings)
    const upcomingRevenue = filteredBookings
      .filter(b => {
        const bookingDate = parseISO(b.date);
        return b.status === 'booked' && 
               b.revenue && 
               (isFuture(bookingDate) || isToday(bookingDate));
      })
      .reduce((sum, b) => sum + (b.revenue?.amount || 0), 0);

    // Calculate ageing breakdown for upcoming revenue
    const ageing: AgeingBucket[] = [
      { range: 'Next 7 days', amount: 0, count: 0 },
      { range: '8-30 days', amount: 0, count: 0 },
      { range: '30+ days', amount: 0, count: 0 }
    ];

    // Get all confirmed upcoming bookings
    const upcomingBookings = filteredBookings.filter(b => {
      const bookingDate = parseISO(b.date);
      return b.status === 'booked' && 
             b.revenue && 
             (isFuture(bookingDate) || isToday(bookingDate));
    });

    upcomingBookings.forEach(booking => {
      if (!booking.revenue) return;
      
      const bookingDate = parseISO(booking.date);
      const amount = booking.revenue.amount;

      if (isAfter(bookingDate, thirtyDaysFromNow)) {
        ageing[2].amount += amount;
        ageing[2].count++;
      } else if (isAfter(bookingDate, sevenDaysFromNow)) {
        ageing[1].amount += amount;
        ageing[1].count++;
      } else {
        ageing[0].amount += amount;
        ageing[0].count++;
      }
    });

    return {
      totalPaidRevenue: paidRevenue,
      totalUpcomingRevenue: upcomingRevenue,
      ageingBreakdown: ageing
    };
  }, [bookings, dateRange]);

  // Export data as CSV
  const handleExport = () => {
    const headers = ['Date', 'Client', 'Status', 'Amount', 'Notes'];
    const rows = bookings
      .filter(booking => {
        const bookingDate = parseISO(booking.date);
        return isWithinInterval(bookingDate, {
          start: startOfMonth(dateRange.from),
          end: endOfMonth(dateRange.to)
        });
      })
      .map(booking => [
        booking.date,
        booking.clientName,
        booking.status,
        booking.revenue?.amount || 0,
        booking.revenue?.notes || ''
      ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Revenue Analytics</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Select
              value={timeframe}
              onValueChange={(value: TimeframeOption) => setTimeframe(value)}
            >
              <SelectTrigger className="w-[140px]">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                {timeframeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {timeframe === 'custom' && (
              <DateRangePicker
                value={customDateRange}
                onChange={setCustomDateRange}
              />
            )}
          </div>

          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExport}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-blue-50/50 border-blue-100">
        <p className="text-sm text-blue-700">
          Showing data from{' '}
          <span className="font-medium">
            {format(dateRange.from, 'PPP')}
          </span>
          {' '}to{' '}
          <span className="font-medium">
            {format(dateRange.to, 'PPP')}
          </span>
        </p>
      </Card>

      {/* Revenue Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100/50">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">
            Total Revenue (Paid)
          </h3>
          <div className="text-3xl font-bold text-blue-700">
            {formatCurrency(totalPaidRevenue)}
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100/50">
          <h3 className="text-lg font-semibold text-emerald-900 mb-4">
            Upcoming Revenue (Confirmed)
          </h3>
          <div className="text-3xl font-bold text-emerald-700">
            {formatCurrency(totalUpcomingRevenue)}
          </div>
        </Card>
      </div>

      {/* Upcoming Revenue Breakdown */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Upcoming Revenue Breakdown
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ageingBreakdown.map((bucket, index) => (
            <div
              key={bucket.range}
              className={cn(
                "p-4 rounded-lg",
                index === 0 && "bg-emerald-50",
                index === 1 && "bg-emerald-50/70",
                index === 2 && "bg-emerald-50/50"
              )}
            >
              <div className="text-sm font-medium mb-2 text-emerald-900">
                {bucket.range}
              </div>
              <div className="text-2xl font-bold mb-1 text-emerald-700">
                {formatCurrency(bucket.amount)}
              </div>
              <div className="text-sm text-emerald-600">
                {bucket.count} booking{bucket.count !== 1 ? 's' : ''}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Monthly Revenue Section */}
      <div className="border-t pt-6">
        <MonthlyRevenue bookings={bookings} />
      </div>
    </div>
  );
}